import { useState } from "react";
import { Plus } from "@phosphor-icons/react";

import { useExploreSections } from "@/hooks/useExploreSections";
import { ContentLayout } from "@/layout";
import { EditExploreSectionModal, EditExploreSubSectionModal } from "@/modals";
import { useNavigateModal } from "@/router/useNavigateModal";
import type { ExploreSection, ExploreSubSection } from "@/types";
import { Button, errorToast, Loader, useToastStore } from "@/ui";

/**
 * ExploreSections Component
 *
 * Displays and manages all Explore sections (e.g., "News & Blog", "Research")
 * with their associated articles (subsections).
 *
 * Features:
 * - View all sections with article counts
 * - Expand/collapse sections to show articles
 * - CRUD operations for sections (create via parent, edit, delete)
 * - CRUD operations for articles/subsections (create, edit, delete)
 * - Real-time data updates via React Query
 * - Confirmation dialogs for destructive actions
 *
 * Security Notes:
 * - All user input (section names, article names, URLs) is automatically escaped by React
 * - Backend validates all inputs including URL schemes (http/https only) and string lengths
 * - Authorization checks enforce admin-only access via Laravel policies
 * - S3 image uploads use presigned URLs with expiration
 */
export const ExploreSections = () => {
  const navigateModal = useNavigateModal();
  const {
    useGetAllExploreSections,
    useDeleteExploreSubSection,
    useDeleteExploreSection,
  } = useExploreSections();
  const { data: sections, isLoading } = useGetAllExploreSections();
  const { mutateAsync: deleteSubSection } = useDeleteExploreSubSection();
  const { mutateAsync: deleteSection } = useDeleteExploreSection();
  const { pushToast } = useToastStore();

  // Track which sections are expanded to show their articles
  const [expandedSections, setExpandedSections] = useState<Set<string>>(
    new Set(),
  );

  // Track which subsection is being edited
  const [editingSubSection, setEditingSubSection] =
    useState<ExploreSubSection | null>(null);

  // Track which section is being edited
  const [editingSection, setEditingSection] = useState<ExploreSection | null>(
    null,
  );

  /**
   * Toggle section expanded/collapsed state
   * @param sectionId - UUID of the section to toggle
   */
  const toggleSection = (sectionId: string) => {
    const newExpanded = new Set(expandedSections);
    if (newExpanded.has(sectionId)) {
      newExpanded.delete(sectionId);
    } else {
      newExpanded.add(sectionId);
    }
    setExpandedSections(newExpanded);
  };

  /**
   * Handle article/subsection deletion with confirmation
   * @param subsection - The subsection to delete
   */
  const handleDeleteSubSection = async (subsection: ExploreSubSection) => {
    // Confirm deletion with user
    if (
      !window.confirm(`Are you sure you want to delete "${subsection.name}"?`)
    ) {
      return;
    }

    try {
      await deleteSubSection(subsection.id);
      void pushToast({
        type: "success",
        title: "Article deleted",
        message: "Article deleted successfully",
      });
    } catch (error) {
      errorToast(error);
    }
  };

  /**
   * Handle section deletion with confirmation
   * Warns user if section contains articles that will also be deleted
   * @param section - The section to delete
   */
  const handleDeleteSection = async (section: ExploreSection) => {
    const articleCount = section.subSections?.length ?? 0;
    const confirmMessage =
      articleCount > 0
        ? `Are you sure you want to delete "${section.name}"? This will also delete ${articleCount} article${articleCount > 1 ? "s" : ""}.`
        : `Are you sure you want to delete "${section.name}"?`;

    if (!window.confirm(confirmMessage)) {
      return;
    }

    try {
      await deleteSection(section.id);
      void pushToast({
        type: "success",
        title: "Section deleted",
        message: "Section deleted successfully",
      });
    } catch (error) {
      errorToast(error);
    }
  };

  // Show loader while fetching data
  if (isLoading) {
    return <Loader />;
  }

  // Show empty state if no sections exist
  if (!sections || sections.length === 0) {
    return (
      <ContentLayout className="flex flex-col gap-y-6">
        <p className="text-neutral-600">
          No explore sections found. Click &quot;New Section&quot; above to
          create one.
        </p>
      </ContentLayout>
    );
  }

  return (
    <ContentLayout className="flex flex-col gap-y-6">
      <div className="space-y-4">
        {sections.map((section: ExploreSection) => {
          const isExpanded = expandedSections.has(section.id);

          return (
            <div
              key={section.id}
              className="rounded-2xl border border-neutral-200 bg-white p-6"
            >
              <div className="mb-4 flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between">
                <div className="flex-1">
                  {/* Section name - React automatically escapes to prevent XSS */}
                  <h3 className="mb-1 text-xl font-bold">{section.name}</h3>
                  <p className="text-sm text-neutral-600">
                    {section.subSections?.length ?? 0} articles
                  </p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => setEditingSection(section)}
                  >
                    Edit Section
                  </Button>
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => handleDeleteSection(section)}
                  >
                    Delete Section
                  </Button>
                  <Button
                    variant="outlined"
                    size="sm"
                    onClick={() => toggleSection(section.id)}
                  >
                    {isExpanded ? "Hide Articles" : "Show Articles"}
                  </Button>
                  <Button
                    size="sm"
                    onClick={() =>
                      navigateModal(`/add-explore-subsection/${section.id}`)
                    }
                  >
                    <Plus size={16} />
                    Add Article
                  </Button>
                </div>
              </div>

              {/* Show articles when expanded */}
              {isExpanded && (
                <div className="border-t pt-4">
                  {section.subSections && section.subSections.length > 0 ? (
                    <div className="space-y-2">
                      {section.subSections.map(
                        (subsection: ExploreSubSection) => (
                          <div
                            key={subsection.id}
                            className="flex flex-col items-start justify-between gap-3 rounded-lg bg-neutral-50 p-4 hover:bg-neutral-100 sm:flex-row"
                          >
                            <div className="flex-1">
                              {/* Article name - React automatically escapes */}
                              <p className="mb-1 font-medium">
                                {subsection.name}
                              </p>
                              {/*
                              External link to article - Backend validates URL is http/https only
                              rel="noopener noreferrer" prevents security issues with target="_blank"
                            */}
                              <a
                                href={subsection.url ?? undefined}
                                target="_blank"
                                rel="noopener noreferrer"
                                className="text-xs text-tertiary-600 hover:underline"
                              >
                                {subsection.url}
                              </a>
                            </div>
                            <div className="flex gap-2">
                              <Button
                                variant="outlined"
                                size="sm"
                                onClick={() => setEditingSubSection(subsection)}
                              >
                                Edit
                              </Button>
                              <Button
                                variant="outlined"
                                size="sm"
                                onClick={() =>
                                  handleDeleteSubSection(subsection)
                                }
                              >
                                Delete
                              </Button>
                            </div>
                          </div>
                        ),
                      )}
                    </div>
                  ) : (
                    <p className="py-4 text-center text-neutral-600">
                      No articles yet. Click &quot;Add Article&quot; to create
                      one.
                    </p>
                  )}
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Edit Modals - Conditionally rendered when editing */}
      {editingSubSection && (
        <EditExploreSubSectionModal
          show={!!editingSubSection}
          onClose={() => setEditingSubSection(null)}
          subsection={editingSubSection}
        />
      )}

      {editingSection && (
        <EditExploreSectionModal
          show={!!editingSection}
          onClose={() => setEditingSection(null)}
          section={editingSection}
        />
      )}
    </ContentLayout>
  );
};
